import type {AppError, Result, ResultWithOauthToken} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {isReleasedNewRegisterFlow} from '../../config/unReleasePage';
import {defaultClient} from '../../lib/defaultClient';
import type {MutationConfig} from '../../lib/react-query';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {UnexpectedError} from '../../utils/unexpectedError';
import type {
  AddressFormValues,
  toAlternateAddressFlatRequestBody,
  toCurrentAddressRequestBody,
} from '../entities/address/schema';
import {
  CheckAcEmailResponseSchema,
  CheckPrivateEmailExistResponseSchema,
  type GeneralProfileRequestSchema,
  GeneralProfileResponse,
  GeneralProfileResponseSchema,
  GetInflowPathSchema,
  GetIntroductionCampaignMessageSchema,
  GetRegisterEnqueteListSchema,
  GetRegisterFollowCompanySchema,
  GetRegisterWishSchema,
  GraduationYearTempResponseSchema,
  IntentionMasterSchema,
  type PostItSkillTempSchema,
  PostRegisterEducationResponseSchema,
  PostRegisterFinishResponseSchema,
  type PostRegisterFinishSchema,
  PostRegisterFollowCompanyResponseSchema,
  type PostRegisterFollowCompanySchema,
  PostRegisterItSkillResponseSchema,
  PostRegisterSkillResponseSchema,
  PostRegisterWishResponseSchema,
  type PostRegisterWishSchema,
  PostStudentCardResponseSchema,
  type PostStudentCardSchema,
  type RegisterAcEmailRequestSchema,
  RegisterAcEmailResponseSchema,
  type RegisterEducationRequestSchema,
  RegisterEducationResponseSchema,
  RegisterItSkillResponseSchema,
  RegisterMailUnsubscribeResponseSchema,
  type RegisterSkillRequestSchema,
  RegisterSkillResponseSchema,
  VerifyAcEmailResponseSchema,
  VerifyEmailResponseSchema,
  VerifyPrivateEmailResponseSchema,
} from '../entities/register/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';

type UsePostRegisterAcMailAuthOptions = {
  config?: MutationConfig<typeof postRegisterAcMailAuth>;
  onSuccessFunc: () => void;
  onErrorFunc: (error: unknown) => void;
};

// CheckPrivateEmailExist
type CheckPrivateEmailExistResponse = z.infer<
  typeof CheckPrivateEmailExistResponseSchema
>;
type ResultCheckPrivateEmailExist = Result<
  CheckPrivateEmailExistResponse,
  AppError
>;

type CheckPrivateEmailExistArgs = {
  version?: string;
  queryParams: {
    email: string;
  };
};

export const checkPrivateEmailExist = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: CheckPrivateEmailExistArgs): Promise<ResultCheckPrivateEmailExist> => {
  const path = createPathWithQuery(
    `/${version}/users/check-email-exists`,
    queryParams,
  );
  const result: Result<CheckPrivateEmailExistResponse, AppError> =
    await defaultClient.get(path, CheckPrivateEmailExistResponseSchema);

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseCheckPrivateEmailExistOptions = {
  config?: UseQueryOptions<ResultCheckPrivateEmailExist>;
  queryParams: {
    email: string;
  };
};

export const useCheckPrivateEmailExist = ({
  config,
  queryParams,
}: UseCheckPrivateEmailExistOptions) => {
  return useQuery<ResultCheckPrivateEmailExist>({
    ...config,
    queryKey: ['checkPrivateEmailExist', queryParams.email],
    async queryFn() {
      return checkPrivateEmailExist({queryParams});
    },
  });
};

// CheckAcEmail
// 大学メールアドレスの送信前チェック。有効性(valid)と在籍中ユーザーとの重複(exists)を判定する。
// メールアドレスをログに残さないためPOSTでリクエストボディから送る。
type CheckAcEmailResponse = z.infer<typeof CheckAcEmailResponseSchema>;
type ResultCheckAcEmail = Result<CheckAcEmailResponse, AppError>;

type CheckAcEmailArgs = {
  version?: string;
  requestBody: {
    email: string;
  };
};

export const checkAcEmail = async ({
  version = DEFAULT_API_VERSION,
  requestBody,
}: CheckAcEmailArgs): Promise<ResultCheckAcEmail> => {
  const result: Result<CheckAcEmailResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/ac-email-check`,
      CheckAcEmailResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseCheckAcEmailOptions = {
  config?: MutationConfig<typeof checkAcEmail>;
};

export const useCheckAcEmail = ({config}: UseCheckAcEmailOptions = {}) => {
  return useMutation({
    mutationFn: checkAcEmail,
    ...config,
  });
};

// RegisterAcEmail
type RegisterAcEmailType = z.infer<typeof RegisterAcEmailRequestSchema>;
type RegisterAcEmailResponse = z.infer<typeof RegisterAcEmailResponseSchema>;
type ResultPostRegisterMailAuthType = Result<RegisterAcEmailResponse, AppError>;

type PostRegisterMailAuthArgs = {
  requestBody: RegisterAcEmailType;
  version?: string;
};

export const postRegisterAcMailAuth = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
}: PostRegisterMailAuthArgs): Promise<ResultPostRegisterMailAuthType> => {
  const result: Result<RegisterAcEmailResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/mail-auth`,
      RegisterAcEmailResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const usePostRegisterAcMailAuth = ({
  config,
  onSuccessFunc,
  onErrorFunc,
}: UsePostRegisterAcMailAuthOptions) => {
  return useMutation({
    mutationFn: postRegisterAcMailAuth,
    onSuccess() {
      onSuccessFunc();
    },
    onError(error) {
      onErrorFunc(error);
    },
    ...config,
  });
};

type RegisterMailUnsubscribeResponse = z.infer<
  typeof RegisterMailUnsubscribeResponseSchema
>;
type ResultPostRegisterMailUnsubscribe = Result<
  RegisterMailUnsubscribeResponse,
  AppError
>;

type PostRegisterMailUnsubscribeArgs = {
  version?: string;
  queryParams: {
    token: string;
  };
};

// 会員登録リマインドメールの配信停止(未ログイン)。token(暗号化email)で対象を特定する。
export const postRegisterMailUnsubscribe = async ({
  queryParams,
  version = DEFAULT_API_VERSION,
}: PostRegisterMailUnsubscribeArgs): Promise<ResultPostRegisterMailUnsubscribe> => {
  const path = createPathWithQuery(
    `/${version}/users/register/mail-unsubscribe`,
    queryParams,
  );
  const result: Result<RegisterMailUnsubscribeResponse, AppError> =
    await defaultClient.post(path, RegisterMailUnsubscribeResponseSchema, {});

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UsePostRegisterMailUnsubscribeOptions = {
  config?: MutationConfig<typeof postRegisterMailUnsubscribe>;
  onSuccessFunc: () => void;
  onErrorFunc: (error: unknown) => void;
};

export const usePostRegisterMailUnsubscribe = ({
  config,
  onSuccessFunc,
  onErrorFunc,
}: UsePostRegisterMailUnsubscribeOptions) => {
  return useMutation({
    mutationFn: postRegisterMailUnsubscribe,
    onSuccess() {
      onSuccessFunc();
    },
    onError(error) {
      onErrorFunc(error);
    },
    ...config,
  });
};

type GetIntroductionCampaignMessageType = z.infer<
  typeof GetIntroductionCampaignMessageSchema
>;

export type ResultGetIntroductionCampaignMessage = Result<
  GetIntroductionCampaignMessageType,
  AppError
>;

type GetIntroductionCampaignMessageArgs = {
  version?: string;
  campaignId: string;
};

export const getIntroductionCampaignMessage = async ({
  version = DEFAULT_API_VERSION,
  campaignId,
}: GetIntroductionCampaignMessageArgs): Promise<ResultGetIntroductionCampaignMessage> => {
  const url = createPathWithQuery(
    `/${version}/users/register/campaign-message`,
    {
      campaignID: campaignId,
    },
  );
  const result: Result<GetIntroductionCampaignMessageType, AppError> =
    await defaultClient.get(url, GetIntroductionCampaignMessageSchema);

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetIntroductionCampaignMessageOptions = {
  config?: UseQueryOptions<ResultGetIntroductionCampaignMessage>;
} & GetIntroductionCampaignMessageArgs;

export const useGetIntroductionCampaignMessage = ({
  config,
  campaignId,
}: UseGetIntroductionCampaignMessageOptions) => {
  return useQuery<ResultGetIntroductionCampaignMessage>({
    ...config,
    queryKey: ['GetIntroductionCampaignMessage', campaignId],
    async queryFn() {
      return getIntroductionCampaignMessage({
        campaignId,
        version: DEFAULT_API_VERSION,
      });
    },
  });
};

// VerifyEmail
type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;
export type ResultVerifyEmail = {
  result: Result<VerifyEmailResponse, AppError>;
  registerTempToken: string | undefined;
};

type VerifyEmailVerifyArgs = {
  version?: string;
  queryParams: {
    mk: string;
  };
};

// Headerのset-cookieからregisterTempTokenを取得する
export const verifyEmail = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: VerifyEmailVerifyArgs): Promise<ResultVerifyEmail> => {
  const path = createPathWithQuery(
    `/${version}/users/register/mail-auth-verify`,
    {
      mk: queryParams.mk,
    },
  );
  const {result, headers} =
    await defaultClient.getWithResponseHeader<VerifyEmailResponse>(
      path,
      VerifyEmailResponseSchema,
    );
  // RegisterTempTokenをセットする
  const registerTempTokenCookie = headers['set-cookie']?.find((cookie) =>
    cookie.includes('registerTempToken'),
  );
  const registerTempTokenValue = registerTempTokenCookie?.split(/[\s=;]/)[1];
  if (registerTempTokenValue === undefined) {
    return {result, registerTempToken: undefined};
  }

  return {result, registerTempToken: registerTempTokenValue};
};

// GeneralProfile
export type GetGeneralProfile = z.infer<typeof GeneralProfileResponseSchema>;
export type ResultGetGeneralProfile = Result<GetGeneralProfile, AppError>;

type GetGeneralProfileArgs = {
  version?: string;
  registerTempToken: string;
};

export const getGeneralProfile = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetGeneralProfileArgs): Promise<ResultGetGeneralProfile> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetGeneralProfile, AppError> = await defaultClient.get(
    `/${version}/users/register/profile-temp`,
    GeneralProfileResponseSchema,
  );

  return result;
};

type UseGetGeneralProfileOptions = {
  config?: UseQueryOptions<ResultGetGeneralProfile>;
  registerTempToken: string;
};

export const useGetGeneralProfile = ({
  config,
  registerTempToken,
}: UseGetGeneralProfileOptions) => {
  return useQuery<ResultGetGeneralProfile>({
    ...config,
    queryKey: ['GetGeneralProfile'],
    async queryFn() {
      return getGeneralProfile({registerTempToken});
    },
  });
};

// GeneralProfile
// フォーム検証スキーマの出力から、送信時に変換する住所・生年月日・メールを差し替える
type PostGeneralProfileRequestBody = Omit<
  z.infer<typeof GeneralProfileRequestSchema>,
  keyof AddressFormValues | 'birthday' | 'acEmail'
> & {
  birthday: string;
  acEmail?: string;
  privateEmail?: string;
} & ReturnType<typeof toCurrentAddressRequestBody> &
  ReturnType<typeof toAlternateAddressFlatRequestBody>;

type PostRegisterGeneralProfileResponse = z.infer<
  typeof GeneralProfileResponse
>;
export type ResultPostGeneralProfile = Result<
  PostRegisterGeneralProfileResponse,
  AppError
>;

type PostGeneralProfileArgs = {
  requestBody: PostGeneralProfileRequestBody;
  version?: string;
  registerTempToken: string;
};

export const postGeneralProfile = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostGeneralProfileArgs): Promise<ResultPostGeneralProfile> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterGeneralProfileResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/profile-temp`,
      GeneralProfileResponse,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

const putGeneralProfile = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostGeneralProfileArgs): Promise<ResultPostGeneralProfile> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterGeneralProfileResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/profile-temp`,
      GeneralProfileResponse,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterProfileOptions = {
  config?: MutationConfig<typeof postGeneralProfile>;
  onSuccessFunc: () => void;
  onErrorFunc: (error: unknown) => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterProfile = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterProfileOptions) => {
  return useMutation({
    mutationFn: method === 'POST' ? postGeneralProfile : putGeneralProfile,
    onSuccess() {
      onSuccessFunc();
    },
    onError(error) {
      onErrorFunc(error);
    },
    ...config,
  });
};

export type GetGraduationYearTempResponse = z.infer<
  typeof GraduationYearTempResponseSchema
>;
export type ResultGetGraduationYearTemp = Result<
  GetGraduationYearTempResponse,
  AppError
>;
type GetGraduationYearTempArgs = {
  version?: string;
  registerTempToken: string;
};
export const getGraduationYearTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetGraduationYearTempArgs): Promise<ResultGetGraduationYearTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetGraduationYearTempResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/graduation-year`,
      GraduationYearTempResponseSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};
type UseGetGraduationYearTempOptions = {
  config?: UseQueryOptions<ResultGetGraduationYearTemp>;
  registerTempToken: string;
} & GetGraduationYearTempArgs;

export const useGetGraduationYearTemp = ({
  config,
  registerTempToken,
}: UseGetGraduationYearTempOptions) => {
  return useQuery<ResultGetGraduationYearTemp>({
    ...config,
    queryKey: ['GetGraduationYearTemp'],
    async queryFn() {
      return getGraduationYearTemp({registerTempToken});
    },
  });
};

// EducationProfile
export type GetEducationTempResponse = z.infer<
  typeof RegisterEducationResponseSchema
>;
type ResultGetEducationTemp = Result<GetEducationTempResponse, AppError>;

type GetEducationTempArgs = {
  version?: string;
  registerTempToken: string;
};

export const getEducationTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetEducationTempArgs): Promise<ResultGetEducationTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetEducationTempResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/education-temp`,
      RegisterEducationResponseSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type useGetEducationTempOptions = {
  config?: UseQueryOptions<ResultGetEducationTemp>;
  registerTempToken: string;
} & GetEducationTempArgs;

export const useGetEducationTemp = ({
  config,
  registerTempToken,
}: useGetEducationTempOptions) => {
  return useQuery<ResultGetEducationTemp>({
    ...config,
    queryKey: ['GetEducationTemp'],
    async queryFn() {
      return getEducationTemp({registerTempToken});
    },
  });
};

export type PostEducationTemp = z.infer<typeof RegisterEducationRequestSchema>;

type PostRegisterEducationResponse = z.infer<
  typeof PostRegisterEducationResponseSchema
>;
export type ResultPostEducation = Result<
  PostRegisterEducationResponse,
  AppError
>;

type PostEducationTempArgs = {
  requestBody: PostEducationTemp;
  version?: string;
  registerTempToken: string;
};

export const postEducationTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostEducationTempArgs): Promise<ResultPostEducation> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterEducationResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/education-temp`,
      PostRegisterEducationResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const putEducationTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostEducationTempArgs): Promise<ResultPostEducation> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterEducationResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/education-temp`,
      PostRegisterEducationResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterEducationOptions = {
  config?: MutationConfig<typeof postEducationTemp>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterEducation = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterEducationOptions) => {
  return useMutation({
    mutationFn: method === 'POST' ? postEducationTemp : putEducationTemp,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

// Skill
export type GetSkillTempResponse = z.infer<typeof RegisterSkillResponseSchema>;
type ResultGetSkillTemp = Result<GetSkillTempResponse, AppError>;

type GetSkillTempArgs = {
  version?: string;
  registerTempToken: string;
};

export const getSkillTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetSkillTempArgs): Promise<ResultGetSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetSkillTempResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/skill-temp`,
      RegisterSkillResponseSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetSkillOptions = {
  config?: UseQueryOptions<ResultGetSkillTemp>;
  registerTempToken: string;
} & GetSkillTempArgs;

export const useGetSkillTemp = ({
  config,
  registerTempToken,
}: UseGetSkillOptions) => {
  return useQuery<ResultGetSkillTemp>({
    ...config,
    queryKey: ['GetSkillTemp'],
    async queryFn() {
      return getSkillTemp({registerTempToken});
    },
  });
};

type PostRegisterSkillResponse = z.infer<
  typeof PostRegisterSkillResponseSchema
>;
export type ResultPostSkillTemp = Result<PostRegisterSkillResponse, AppError>;

export type PostSkillTemp = z.infer<typeof RegisterSkillRequestSchema>;

type PostSkillTempArgs = {
  requestBody: PostSkillTemp;
  version?: string;
  registerTempToken: string;
};

export const postSkillTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostSkillTempArgs): Promise<ResultPostSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterSkillResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/skill-temp`,
      PostRegisterSkillResponseSchema,
      requestBody,
    );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const putSkillTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostSkillTempArgs): Promise<ResultPostSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterSkillResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/skill-temp`,
      PostRegisterSkillResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterSkillOptions = {
  config?: MutationConfig<typeof postSkillTemp>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterSkill = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterSkillOptions) => {
  return useMutation({
    mutationFn: method === 'POST' ? postSkillTemp : putSkillTemp,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

//
//
// ItSkill
export type GetItSkillTempResponse = z.infer<
  typeof RegisterItSkillResponseSchema
>;
export type ResultGetItSkillTemp = Result<GetItSkillTempResponse, AppError>;

type GetItSkillTempArgs = {
  version?: string;
  registerTempToken: string;
};

export const getItSkillTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetItSkillTempArgs): Promise<ResultGetItSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetItSkillTempResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/it-skill-temp`,
      RegisterItSkillResponseSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetItSkillTempOptions = {
  config?: UseQueryOptions<ResultGetItSkillTemp>;
  registerTempToken: string;
} & GetItSkillTempArgs;

export const useGetItSkillTemp = ({
  config,
  registerTempToken,
}: UseGetItSkillTempOptions) => {
  return useQuery<ResultGetItSkillTemp>({
    ...config,
    queryKey: ['GetItSkillTemp'],
    async queryFn() {
      return getItSkillTemp({registerTempToken});
    },
  });
};

type PostRegisterItSkillResponse = z.infer<
  typeof PostRegisterSkillResponseSchema
>;
export type ResultPostItSkillTemp = Result<
  PostRegisterItSkillResponse,
  AppError
>;

export type PostItSkillTemp = z.infer<typeof PostItSkillTempSchema>;

type PostItSkillTempArgs = {
  requestBody: PostItSkillTemp;
  version?: string;
  registerTempToken: string;
};

export const postItSkillTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostItSkillTempArgs): Promise<ResultPostItSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterItSkillResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/it-skill-temp`,
      PostRegisterItSkillResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const putItSkillTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostItSkillTempArgs): Promise<ResultPostItSkillTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterItSkillResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/it-skill-temp`,
      PostRegisterItSkillResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterItSkillOptions = {
  config?: MutationConfig<typeof postItSkillTemp>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterItSkill = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterItSkillOptions) => {
  return useMutation({
    mutationFn: method === 'POST' ? postItSkillTemp : putItSkillTemp,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

//
//
// Wish
export type GetWishTempResponse = z.infer<typeof GetRegisterWishSchema>;
export type ResultGetWishTemp = Result<GetWishTempResponse, AppError>;

type GetWishTempArgs = {
  version?: string;
  registerTempToken: string;
};

export const getWishTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetWishTempArgs): Promise<ResultGetWishTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetWishTempResponse, AppError> = await defaultClient.get(
    `/${version}/users/register/wish-temp`,
    GetRegisterWishSchema,
  );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type useGetWishTempOptions = {
  config?: UseQueryOptions<ResultGetWishTemp>;
  registerTempToken: string;
} & GetWishTempArgs;

export const useGetWishTemp = ({
  config,
  registerTempToken,
}: useGetWishTempOptions) => {
  return useQuery<ResultGetWishTemp>({
    ...config,
    queryKey: ['GetWishTemp'],
    async queryFn() {
      return getWishTemp({registerTempToken});
    },
  });
};

export type PostWishTemp = z.infer<typeof PostRegisterWishSchema>;

type PostWishTempResponse = z.infer<typeof PostRegisterWishResponseSchema>;
export type ResultPostWishTemp = Result<PostWishTempResponse, AppError>;

type PostWishTempArgs = {
  requestBody: PostWishTemp;
  version?: string;
  registerTempToken: string;
};

export const postWishTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostWishTempArgs): Promise<ResultPostWishTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostWishTempResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/wish-temp`,
      PostRegisterWishResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const putWishTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostWishTempArgs): Promise<ResultPostWishTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostWishTempResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/wish-temp`,
      PostRegisterWishResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterWishOptions = {
  config?: MutationConfig<typeof postWishTemp>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterWish = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterWishOptions) => {
  return useMutation({
    mutationFn: method === 'POST' ? postWishTemp : putWishTemp,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

export type GetFollowCompanyTempResponse = z.infer<
  typeof GetRegisterFollowCompanySchema
>;
export type ResultGetFollowCompanyTemp = Result<
  GetFollowCompanyTempResponse,
  AppError
>;

type GetFollowCompanyTempArgs = {
  version?: string;
  registerTempToken: string;
};

export const getFollowCompanyTemp = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetFollowCompanyTempArgs): Promise<ResultGetFollowCompanyTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetFollowCompanyTempResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/follow-company-temp`,
      GetRegisterFollowCompanySchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetFollowCompanyTempOptions = {
  config?: UseQueryOptions<ResultGetFollowCompanyTemp>;
  registerTempToken: string;
} & GetFollowCompanyTempArgs;

export const useGetFollowCompanyTemp = ({
  config,
  registerTempToken,
}: UseGetFollowCompanyTempOptions) => {
  return useQuery<ResultGetFollowCompanyTemp>({
    ...config,
    queryKey: ['GetFollowCompanyTemp'],
    async queryFn() {
      return getFollowCompanyTemp({registerTempToken});
    },
  });
};

export type PostFollowCompanyResponse = z.infer<
  typeof PostRegisterFollowCompanyResponseSchema
>;
export type ResultPostFollowCompanyTemp = Result<
  PostFollowCompanyResponse,
  AppError
>;

type PostFollowCompanyTempArgs = {
  requestBody: z.infer<typeof PostRegisterFollowCompanySchema>;
  version?: string;
  registerTempToken: string;
};

export const postFollowCompanyTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostFollowCompanyTempArgs): Promise<ResultPostFollowCompanyTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostFollowCompanyResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/follow-company-temp`,
      PostRegisterFollowCompanyResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const putFollowCompanyTemp = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostFollowCompanyTempArgs): Promise<ResultPostFollowCompanyTemp> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostFollowCompanyResponse, AppError> =
    await defaultClient.put(
      `/${version}/users/register/follow-company-temp`,
      PostRegisterFollowCompanyResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterFollowCompanyOptions = {
  config?: MutationConfig<typeof postFollowCompanyTemp>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
  method: 'POST' | 'PUT';
};

export const usePostRegisterFollowCompany = ({
  config,
  onSuccessFunc,
  onErrorFunc,
  method,
}: usePostRegisterFollowCompanyOptions) => {
  return useMutation({
    mutationFn:
      method === 'POST' ? postFollowCompanyTemp : putFollowCompanyTemp,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

/** GET /3.0/service/master/intentions */
type IntentionMaster = z.infer<typeof IntentionMasterSchema>;
export type ResultGetIntentionMaster = Result<IntentionMaster, AppError>;

type GetIntentionMasterParams = {
  version?: string;
  queryParams: {
    q?: string;
  };
};

export const getIntentionMaster = async ({
  version = DEFAULT_API_VERSION,
  queryParams = {
    q: '',
  },
}: GetIntentionMasterParams): Promise<ResultGetIntentionMaster> => {
  const path = createPathWithQuery(`/${version}/service/master/intentions`, {
    q: queryParams.q ?? '',
  });
  const result: Result<IntentionMaster, AppError> = await defaultClient.get(
    path,
    IntentionMasterSchema,
  );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetIntentionMasterOptions = {
  config?: UseQueryOptions<ResultGetIntentionMaster>;
  queryParams: {
    q?: string;
  };
} & GetIntentionMasterParams;

export const useGetIntentionMaster = ({
  config,
  queryParams = {
    q: '',
  },
}: UseGetIntentionMasterOptions) => {
  return useQuery<ResultGetIntentionMaster>({
    ...config,
    queryKey: ['GetIntentionMaster', queryParams.q],
    async queryFn() {
      return getIntentionMaster({queryParams});
    },
  });
};

/** GET /3.0/users/register/enquete-info */
export type GetRegisterEnqueteList = z.infer<
  typeof GetRegisterEnqueteListSchema
>;
export type ResultGetRegisterEnqueteList = Result<
  GetRegisterEnqueteList,
  AppError
>;

type GetRegisterEnqueteListParams = {
  version?: string;
};

export const getRegisterEnqueteList = async ({
  version = DEFAULT_API_VERSION,
}: GetRegisterEnqueteListParams): Promise<ResultGetRegisterEnqueteList> => {
  const result: Result<GetRegisterEnqueteList, AppError> =
    await defaultClient.get(
      `/${version}/users/register/enquete-info`,
      GetRegisterEnqueteListSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetRegisterEnqueteListOptions = {
  config?: UseQueryOptions<ResultGetRegisterEnqueteList>;
} & GetRegisterEnqueteListParams;

export const useGetRegisterEnqueteList = ({
  config = {},
}: UseGetRegisterEnqueteListOptions) => {
  return useQuery<ResultGetRegisterEnqueteList>({
    ...config,
    queryKey: ['GetRegisterEnqueteList'],
    async queryFn() {
      return getRegisterEnqueteList({});
    },
  });
};

export type PostRegisterFinishResponse = z.infer<
  typeof PostRegisterFinishResponseSchema
>;

export type ResultPostRegisterFinish = Result<
  PostRegisterFinishResponse,
  AppError
>;

type PostRegisterFinishArgs = {
  requestBody: z.infer<typeof PostRegisterFinishSchema>;
  version?: string;
  registerTempToken: string;
};

export const postRegisterFinish = async ({
  requestBody,
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: PostRegisterFinishArgs): Promise<ResultPostRegisterFinish> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<PostRegisterFinishResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/finish?withRegisterGssktNext=1`, // ネクスト同時連携リリース時のエラー回避のためクエリパラメータを追加
      PostRegisterFinishResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostRegisterFinishOptions = {
  config?: MutationConfig<typeof postRegisterFinish>;
  onSuccessFunc: ({
    accessToken,
    accessTokenExpiresIn,
    refreshToken,
    refreshTokenExpiresIn,
  }: PostRegisterFinishResponse) => void;
  onErrorFunc: (error: unknown) => void;
};

export const usePostRegisterFinish = ({
  config,
  onSuccessFunc,
  onErrorFunc,
}: usePostRegisterFinishOptions) => {
  return useMutation({
    mutationFn: postRegisterFinish,
    onSuccess(data) {
      if (!data.ok) {
        throw new UnexpectedError(data.error);
      }

      onSuccessFunc(data.value);
    },
    onError(error) {
      onErrorFunc(error);
    },
    ...config,
  });
};

type PostStudentCardBody = z.infer<typeof PostStudentCardSchema>;

type PostStudentCardResponse = z.infer<typeof PostStudentCardResponseSchema>;
type ResultPostStudentCard = Result<PostStudentCardResponse, AppError>;

type PostStudentCardArgs = {
  requestBody: PostStudentCardBody;
  cookies?: Partial<Record<string, string>>;
  version?: string;
};

export const postStudentCard = async ({
  requestBody,
  cookies,
  version = DEFAULT_API_VERSION,
}: PostStudentCardArgs): Promise<ResultPostStudentCard> => {
  // 新フローはログインユーザーの認証を載せて送信し、氏名・メールはサーバー側でアカウントから設定する
  if (isReleasedNewRegisterFlow()) {
    const request = async (accessToken: string) => {
      defaultClient.setToken(accessToken);
      return defaultClient.post(
        `/${version}/users/register/student-card`,
        PostStudentCardResponseSchema,
        requestBody,
      );
    };

    // 新フローはログインユーザーの認証が必須。cookie が無いと静かに認証失敗するため明示的にエラーにする
    if (!cookies) {
      throw new Error('cookies is required for the new register flow');
    }

    const result = await sendApiRequest<PostStudentCardResponse>({
      request,
      cookies,
      version,
    });

    if (!result.ok) {
      throw new Error(result.error);
    }

    return result;
  }

  const result: Result<PostStudentCardResponse, AppError> =
    await defaultClient.post(
      `/${version}/users/register/student-card`,
      PostStudentCardResponseSchema,
      requestBody,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type usePostStudentCardOptions = {
  config?: MutationConfig<typeof postStudentCard>;
  onSuccessFunc: () => void;
  onErrorFunc: () => void;
};

export const usePostStudentCard = ({
  config,
  onSuccessFunc,
  onErrorFunc,
}: usePostStudentCardOptions) => {
  return useMutation({
    mutationFn: postStudentCard,
    onSuccess() {
      onSuccessFunc();
    },
    onError() {
      onErrorFunc();
    },
    ...config,
  });
};

// VerifyPrivateEmail
type VerifyPrivateEmailResponse = z.infer<
  typeof VerifyPrivateEmailResponseSchema
>;

export type ResultVerifyPrivateEmail = ResultWithOauthToken<
  VerifyPrivateEmailResponse,
  AppError
>;

type verifyPrivateEmailArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    mail_key: string;
  };
};

export const verifyPrivateEmail = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: verifyPrivateEmailArgs): Promise<ResultVerifyPrivateEmail> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<VerifyPrivateEmailResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/users/register/mail-private-verify`,
      {
        mail_key: queryParams.mail_key,
      },
    );
    return defaultClient.get(path, VerifyPrivateEmailResponseSchema);
  };

  const result = await sendApiRequest<VerifyPrivateEmailResponse>({
    cookies,
    request,
    allowReturnError: true,
  });

  return result;
};

// VerifyAcEmail
type VerifyAcEmailResponse = z.infer<typeof VerifyAcEmailResponseSchema>;

export type ResultVerifyAcEmail = ResultWithOauthToken<
  VerifyAcEmailResponse,
  AppError
>;

type verifyAcEmailArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    mail_key: string;
  };
};

export const verifyAcEmail = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: verifyAcEmailArgs): Promise<ResultVerifyAcEmail> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<VerifyAcEmailResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/users/register/mail-ac-verify`,
      {
        mail_key: queryParams.mail_key,
      },
    );
    return defaultClient.get(path, VerifyAcEmailResponseSchema);
  };

  const result = await sendApiRequest<VerifyAcEmailResponse>({
    cookies,
    request,
    allowReturnError: true,
  });

  return result;
};

export type GetInflowPathResponse = z.infer<typeof GetInflowPathSchema>;

export type ResultGetInflowPath = Result<GetInflowPathResponse, AppError>;

type GetInflowPathArgs = {
  version?: string;
  registerTempToken: string;
};

export const getInflowPath = async ({
  version = DEFAULT_API_VERSION,
  registerTempToken,
}: GetInflowPathArgs): Promise<ResultGetInflowPath> => {
  defaultClient.setRegisterTempToken(registerTempToken);

  const result: Result<GetInflowPathResponse, AppError> =
    await defaultClient.get(
      `/${version}/users/register/inflow-path`,
      GetInflowPathSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetInflowPathOptions = {
  config?: UseQueryOptions<ResultGetInflowPath>;
  registerTempToken: string;
} & GetInflowPathArgs;

export const useGetInflowPath = ({
  config,
  registerTempToken,
}: UseGetInflowPathOptions) => {
  return useQuery<ResultGetInflowPath>({
    ...config,
    queryKey: ['GetInflowPath'],
    async queryFn() {
      return getInflowPath({registerTempToken});
    },
  });
};
